import { z } from "zod";

/**
 * Client tone-of-voice ingestion vocabulary: a Version's own lifecycle, who
 * acted on it, and how one uploaded source moves from bytes to embeddings.
 * The brand-voice vocabulary those sources produce lives in `tov-voice`; the
 * mapping run that produces it lives in `tov-mapping`.
 */

export const TOV_VERSION_STATUSES = ["draft", "active", "archived"] as const;
export const TOV_VERSION_STATUS = {
  DRAFT: "draft",
  ACTIVE: "active",
  ARCHIVED: "archived",
} as const satisfies Record<string, TovVersionStatus>;
export const TovVersionStatusSchema = z.enum(TOV_VERSION_STATUSES);
export type TovVersionStatus = z.infer<typeof TovVersionStatusSchema>;

export const TOV_ACTOR_TYPES = ["admin", "llm", "worker", "system"] as const;
export const TOV_ACTOR_TYPE = {
  ADMIN: "admin",
  LLM: "llm",
  WORKER: "worker",
  SYSTEM: "system",
} as const satisfies Record<string, TovActorType>;
export const TovActorTypeSchema = z.enum(TOV_ACTOR_TYPES);
export type TovActorType = z.infer<typeof TovActorTypeSchema>;

export const TOV_SOURCE_TYPES = [
  "pdf",
  "image",
  "text",
  "url",
  "chat",
  "manual_edit",
] as const;
export const TOV_SOURCE_TYPE = {
  PDF: "pdf",
  IMAGE: "image",
  TEXT: "text",
  URL: "url",
  CHAT: "chat",
  MANUAL_EDIT: "manual_edit",
} as const satisfies Record<string, TovSourceType>;
export const TovSourceTypeSchema = z.enum(TOV_SOURCE_TYPES);
export type TovSourceType = z.infer<typeof TovSourceTypeSchema>;

export const TOV_SOURCE_STATUSES = [
  "created",
  "uploaded",
  "parse_requested",
  "parsing",
  "parsed",
  "chunked",
  "embedded",
  "mapped",
  "failed",
  "superseded",
] as const;
export const TOV_SOURCE_STATUS = {
  CREATED: "created",
  UPLOADED: "uploaded",
  PARSE_REQUESTED: "parse_requested",
  PARSING: "parsing",
  PARSED: "parsed",
  CHUNKED: "chunked",
  EMBEDDED: "embedded",
  MAPPED: "mapped",
  FAILED: "failed",
  SUPERSEDED: "superseded",
} as const satisfies Record<string, TovSourceStatus>;
export const TovSourceStatusSchema = z.enum(TOV_SOURCE_STATUSES);
export type TovSourceStatus = z.infer<typeof TovSourceStatusSchema>;

export const TOV_PARSE_STATUSES = [
  "queued",
  "running",
  "completed",
  "failed",
  "cancelled",
] as const;
export const TOV_PARSE_STATUS = {
  QUEUED: "queued",
  RUNNING: "running",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled",
} as const satisfies Record<string, TovParseStatus>;
export const TovParseStatusSchema = z.enum(TOV_PARSE_STATUSES);
export type TovParseStatus = z.infer<typeof TovParseStatusSchema>;

export const LLAMAPARSE_EXPAND_FIELDS = [
  "markdown",
  "text",
  "items",
  "metadata",
  "job_metadata",
] as const;
export const LlamaParseExpandFieldSchema = z.enum(LLAMAPARSE_EXPAND_FIELDS);
export type LlamaParseExpandField = z.infer<typeof LlamaParseExpandFieldSchema>;

// A source whose text has been extracted and is available to the mapper.
// `mapped` stays usable: mapping consumes the text, it does not discard it.
export const TOV_USABLE_SOURCE_STATUSES: readonly TovSourceStatus[] = [
  "parsed",
  "chunked",
  "embedded",
  "mapped",
];

// A source still on its way in. Neither usable yet nor failed, so coverage
// counts it as outstanding rather than missing.
export const TOV_PROCESSING_SOURCE_STATUSES: readonly TovSourceStatus[] = [
  "created",
  "uploaded",
  "parse_requested",
  "parsing",
];
